import { create } from "zustand";
import { registerScreenRecordingSession } from "./screen-recording-store";

import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import {
  appendInkPoint,
  storedInkPoints,
} from "@/lib/sketch-stroke/user-ink-stroke";
import { boundVoiceAttentionInk } from "./voice-attention-budget";
import { buildVoiceAttentionContextFromState } from "./voice-attention-context";
import {
  type NativeTimedSpeechSegment,
  type TimedSpeechSegment,
  rebaseTimedSpeechSegments,
} from "./voice-speech-timing";

/** The newest chapters a session keeps; older ones are evicted. */
export const VOICE_ATTENTION_MAX_CHAPTERS = 6;
const MAX_TRANSCRIPT_SNAPSHOTS = 24;
const MAX_TRANSCRIPT_CHARS = 12_000;

/** A stored ink point. `x`/`y` are streamlined at ingestion (within a few
 *  points of the raw pointer) and `p` is the final pen pressure; prompts and
 *  context read the same streamlined position. Stored points never change. */
export type VoiceAttentionPoint = {
  tMs: number;
  displayId: number;
  x: number;
  y: number;
  p: number;
  chapterId: string;
  revision: number;
};

export type VoiceAttentionStroke = {
  id: string;
  startedMs: number;
  endedMs?: number;
  points: VoiceAttentionPoint[];
};

export type TranscriptSnapshot = {
  kind: "partial" | "commit" | "final";
  tMs: number;
  text: string;
  nativeSpeechSegments: NativeTimedSpeechSegment[];
  speechSegments: TimedSpeechSegment[];
};

export type VoiceAttentionViewTrigger = "initial" | "scroll" | "click";

export type VoiceAttentionViewRevision = {
  displayId: number;
  revision: number;
  openedMs: number;
  settledMs?: number;
  changing: boolean;
  trigger: VoiceAttentionViewTrigger;
};

export type VoiceAttentionChapter = {
  id: string;
  displayId: number;
  revision: number;
  openedMs: number;
  settledMs?: number;
  closedMs?: number;
  trigger: VoiceAttentionViewTrigger;
};

export type VoiceAttentionChapterAttachment = {
  chapterId: string;
  imageId: string;
  screen: ScreenshotScreenMeta;
};

export type VoiceAttentionContext = {
  version: 2;
  recording_id: string;
  activation: {
    display_id: number | null;
    x: number;
    y: number;
    source: "speak";
  };
  chapters: Array<{
    chapter_id: string;
    display_id: number;
    revision: number;
    trigger: VoiceAttentionViewTrigger;
    opened_ms: number;
    settled_ms?: number;
    closed_ms?: number;
    screen: number;
    image_id: string;
  }>;
  strokes: Array<{
    stroke_id: string;
    chapter_id: string;
    started_ms: number;
    ended_ms: number;
    display_id: number;
    screen: number;
    image_id?: string;
    speech_window: { start_ms: number; end_ms: number };
    points: Array<{ t_ms: number; x: number; y: number }>;
  }>;
  transcript_timeline: Array<{
    kind: TranscriptSnapshot["kind"];
    t_ms: number;
    text: string;
    speech_segments?: TimedSpeechSegment[];
  }>;
};

export type VoiceAttentionState = {
  recordingId: string | null;
  clockStartedAt: number;
  originUptimeMs: number | null;
  activation: VoiceAttentionContext["activation"] | null;
  strokes: VoiceAttentionStroke[];
  views: Record<string, VoiceAttentionViewRevision>;
  chapters: VoiceAttentionChapter[];
  transcriptTimeline: TranscriptSnapshot[];
  /** Recordings of earlier live-voice segments merged into this prompt. */
  mergedRecordingIds?: string[];
};

const INITIAL_STATE: VoiceAttentionState = {
  recordingId: null,
  clockStartedAt: 0,
  originUptimeMs: null,
  activation: null,
  strokes: [],
  views: {},
  chapters: [],
  transcriptTimeline: [],
};

export const useVoiceAttentionStore = create<VoiceAttentionState>(
  () => INITIAL_STATE,
);

export function beginVoiceAttentionSession(
  recordingId: string,
  activation: VoiceAttentionContext["activation"],
): void {
  registerScreenRecordingSession(recordingId, "voice");
  const displayId = activation.display_id;
  useVoiceAttentionStore.setState({
    recordingId,
    clockStartedAt: performance.now(),
    originUptimeMs: null,
    activation,
    strokes: [],
    views:
      displayId === null
        ? {}
        : {
            [displayId]: {
              displayId,
              revision: 0,
              openedMs: 0,
              settledMs: 0,
              changing: false,
              trigger: "initial",
            },
          },
    transcriptTimeline: [],
    chapters: [],
  });
}

function defaultView(displayId: number): VoiceAttentionViewRevision {
  return {
    displayId,
    revision: 0,
    openedMs: 0,
    settledMs: 0,
    changing: false,
    trigger: "initial",
  };
}

function chapterId(displayId: number, revision: number): string {
  return `${displayId}:${revision}`;
}

export type NativeVoiceAttentionViewChange = {
  recordingId: string;
  clipId?: string | null;
  phase: "start" | "settled";
  revision: number;
  tMs: number;
  displayId: number | null;
  x: number;
  y: number;
  kind: "scroll" | "click";
};

export function ingestVoiceAttentionViewChange(
  event: NativeVoiceAttentionViewChange,
): VoiceAttentionChapter | null {
  const state = useVoiceAttentionStore.getState();
  if (
    state.recordingId !== event.recordingId ||
    event.displayId === null ||
    !Number.isInteger(event.revision) ||
    event.revision < 0
  ) {
    return null;
  }
  const displayKey = String(event.displayId);
  const current = state.views[displayKey] ?? defaultView(event.displayId);
  const tMs = Math.max(0, Math.round(event.tMs));
  if (event.revision < current.revision) return null;

  if (event.phase === "start") {
    const views = {
      ...state.views,
      [displayKey]: {
        displayId: event.displayId,
        revision: event.revision,
        openedMs: tMs,
        changing: true,
        trigger: event.kind,
      },
    };
    const chapters = state.chapters.map((chapter) =>
      chapter.displayId === event.displayId &&
      chapter.revision < event.revision &&
      chapter.closedMs === undefined
        ? { ...chapter, closedMs: tMs }
        : chapter,
    );
    useVoiceAttentionStore.setState({ views, chapters });
    return null;
  }

  if (event.revision !== current.revision) return null;
  const settledView = { ...current, changing: false, settledMs: tMs };
  const chapters = state.chapters.map((chapter) =>
    chapter.displayId === event.displayId && chapter.revision === event.revision
      ? { ...chapter, settledMs: tMs }
      : chapter,
  );
  useVoiceAttentionStore.setState({
    views: { ...state.views, [displayKey]: settledView },
    chapters,
  });
  return (
    chapters.find(
      (chapter) =>
        chapter.displayId === event.displayId &&
        chapter.revision === event.revision,
    ) ?? null
  );
}

export function ingestVoiceAttentionGesture(event: {
  recordingId: string;
  strokeId: string;
  phase: "start" | "move" | "end";
  tMs: number;
  displayId: number | null;
  x: number;
  y: number;
}): { chapter: VoiceAttentionChapter; evictedChapterIds: string[] } | null {
  const state = useVoiceAttentionStore.getState();
  if (
    state.recordingId !== event.recordingId ||
    event.displayId === null ||
    !Number.isFinite(event.x) ||
    !Number.isFinite(event.y)
  ) {
    return null;
  }
  const viewKey = String(event.displayId);
  const view = state.views[viewKey] ?? defaultView(event.displayId);
  if (view.changing) return null;
  const pointChapterId = chapterId(event.displayId, view.revision);
  const tMs = Math.max(0, Math.round(event.tMs));
  let chapters = [...state.chapters];
  let chapter = chapters.find((candidate) => candidate.id === pointChapterId);
  if (!chapter) {
    chapter = {
      id: pointChapterId,
      displayId: event.displayId,
      revision: view.revision,
      openedMs: view.openedMs,
      settledMs: view.settledMs,
      trigger: view.trigger,
    };
    chapters.push(chapter);
  }
  const evictedChapterIds = chapters
    .slice(0, Math.max(0, chapters.length - VOICE_ATTENTION_MAX_CHAPTERS))
    .map((candidate) => candidate.id);
  if (evictedChapterIds.length > 0) {
    const evicted = new Set(evictedChapterIds);
    chapters = chapters.filter((candidate) => !evicted.has(candidate.id));
  }
  const strokes = state.strokes
    .map((stroke) => ({
      ...stroke,
      points: stroke.points.filter(
        (candidate) => !evictedChapterIds.includes(candidate.chapterId),
      ),
    }))
    .filter((stroke) => stroke.points.length > 0);
  let stroke = strokes.find((candidate) => candidate.id === event.strokeId);
  if (!stroke && event.phase === "start") {
    stroke = { id: event.strokeId, startedMs: tMs, points: [] };
    strokes.push(stroke);
  }
  if (!stroke) return null;
  // A run restarts raw where the stroke enters another display or view.
  const last = stroke.points[stroke.points.length - 1];
  const previous =
    last?.displayId === event.displayId && last.revision === view.revision
      ? last
      : null;
  // A release also stores the point where the pen lifted.
  const inks = storedInkPoints(
    previous,
    { x: event.x, y: event.y, tMs },
    event.phase === "end",
  );
  // A move under 1 pt stores nothing, so nothing changed.
  if (inks.length === 0 && event.phase !== "end") {
    return { chapter, evictedChapterIds };
  }
  for (const ink of inks) {
    stroke.points = appendInkPoint(stroke.points, {
      tMs,
      displayId: event.displayId,
      x: ink.x,
      y: ink.y,
      p: ink.p,
      chapterId: pointChapterId,
      revision: view.revision,
    });
  }
  if (event.phase === "end") stroke.endedMs = tMs;
  useVoiceAttentionStore.setState({
    strokes: boundVoiceAttentionInk(strokes, stroke.id),
    chapters,
    views:
      state.views[viewKey] === undefined
        ? { ...state.views, [viewKey]: view }
        : state.views,
  });
  return { chapter, evictedChapterIds };
}

export function recordVoiceAttentionTranscript(
  kind: TranscriptSnapshot["kind"],
  text: string,
  speechSegments?: NativeTimedSpeechSegment[],
  target?: VoiceAttentionState,
): void {
  const trimmed = text.trim();
  const state = target ?? useVoiceAttentionStore.getState();
  if (!state.recordingId || !trimmed) return;
  const last = state.transcriptTimeline[state.transcriptTimeline.length - 1];
  const nativeSpeechSegments =
    speechSegments && speechSegments.length > 0
      ? speechSegments
      : (last?.nativeSpeechSegments ?? []);
  const incomingTiming = rebaseTimedSpeechSegments(
    nativeSpeechSegments,
    state.originUptimeMs,
  );
  const rebased =
    incomingTiming.length > 0 ? incomingTiming : (last?.speechSegments ?? []);
  const sameTiming =
    last?.speechSegments.length === rebased.length &&
    last.speechSegments.every((segment, index) => {
      const candidate = rebased[index];
      return (
        candidate?.text === segment.text &&
        candidate.start_ms === segment.start_ms &&
        candidate.end_ms === segment.end_ms
      );
    });
  if (last?.text === trimmed && last.kind === kind && sameTiming) return;
  const next = [
    ...state.transcriptTimeline.map((snapshot) => ({
      ...snapshot,
      nativeSpeechSegments: [],
      speechSegments: [],
    })),
    {
      kind,
      tMs: Math.max(0, Math.round(performance.now() - state.clockStartedAt)),
      text: trimmed.slice(0, 2_000),
      nativeSpeechSegments,
      speechSegments: rebased,
    },
  ];
  let charCount = 0;
  const transcriptTimeline = next
    .slice(-MAX_TRANSCRIPT_SNAPSHOTS)
    .reverse()
    .flatMap((snapshot) => {
      if (charCount + snapshot.text.length > MAX_TRANSCRIPT_CHARS) return [];
      charCount += snapshot.text.length;
      return [snapshot];
    })
    .reverse();
  if (target) {
    target.transcriptTimeline = transcriptTimeline;
  } else {
    useVoiceAttentionStore.setState({ transcriptTimeline });
  }
}

export function setVoiceAttentionOrigin(
  recordingId: string,
  originUptimeMs: number,
  target?: VoiceAttentionState,
): void {
  if (!Number.isFinite(originUptimeMs) || originUptimeMs < 0) return;
  const state = target ?? useVoiceAttentionStore.getState();
  if (state.recordingId !== recordingId) return;
  const transcriptTimeline = state.transcriptTimeline.map((snapshot) => ({
    ...snapshot,
    speechSegments: rebaseTimedSpeechSegments(
      snapshot.nativeSpeechSegments,
      originUptimeMs,
    ),
  }));
  if (target) {
    target.originUptimeMs = originUptimeMs;
    target.transcriptTimeline = transcriptTimeline;
  } else {
    useVoiceAttentionStore.setState({ originUptimeMs, transcriptTimeline });
  }
}

export function voiceAttentionElapsedMs(): number {
  const startedAt = useVoiceAttentionStore.getState().clockStartedAt;
  return startedAt > 0
    ? Math.max(0, Math.round(performance.now() - startedAt))
    : 0;
}

export function buildVoiceAttentionContext(
  attachments: VoiceAttentionChapterAttachment[],
  snapshot: VoiceAttentionState = useVoiceAttentionStore.getState(),
): VoiceAttentionContext | null {
  return buildVoiceAttentionContextFromState(attachments, snapshot);
}

export function hasVoiceAttentionStrokes(
  snapshot: VoiceAttentionState = useVoiceAttentionStore.getState(),
): boolean {
  return snapshot.strokes.length > 0;
}

export function clearVoiceAttentionSession(): void {
  useVoiceAttentionStore.setState(INITIAL_STATE);
}
